// Updated ProfileCard.jsx
import React, { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from '@/context/AuthContext';  // Add this import
import {
  Pencil,
  Check,
  X,
  MapPin,
  Briefcase,
  School,
  Moon,
  Sun,
  CalendarClock,
  Building2,
} from "lucide-react";
import AboutSection from "@/components/profile/AboutSection";
import ExperienceSection from "@/components/profile/ExperienceSection";
import AchievementsSection from "@/components/profile/AchievementsSection";
import SkillsSection from "@/components/profile/SkillsSection";
import ProfileInfo from "./ProfileInfo";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

import { Input } from "@/components/ui/input";
import { ThreeDots } from "react-loader-spinner";
import { getUserProfile, updateUserProfileSection, updateUserBasicInfo } from '@/lib/profileHelpers';

const ProfileCard = ({ initialProfile }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains("dark")
  );
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState({
    ...initialProfile,
    college: "Shri Ramdeobaba College of Engineering and Management", // Add default college
    profilePic: initialProfile.profilePic || "/android-chrome-512x512.png", // Default profile image
    name: initialProfile.name,  // Ensure these values are used
    department: initialProfile.department,
    batch: initialProfile.batch,
  });
  const fileInputRef = useRef(null);
  const [imageHover, setImageHover] = useState(false);

  // Add useEffect to update profile when initialProfile changes
  useEffect(() => {
    setProfile(prev => ({
      ...prev,
      name: initialProfile.name,
      department: initialProfile.department,
      batch: initialProfile.batch,
    }));
  }, [initialProfile]);

  // Fetch user profile data on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      
      setLoading(true);
      const result = await getUserProfile(user.uid);
      
      if (result.success) {
        setProfile(prev => ({
          ...initialProfile,
          ...result.data,
          timeline: Array.isArray(result.data.timeline) ? result.data.timeline : [],
          college: "Shri Ramdeobaba College of Engineering and Management",
          profilePic: result.data.profilePic || initialProfile.profilePic || "/android-chrome-512x512.png",
        }));
      }
      setLoading(false);
    };

    fetchUserProfile();
  }, [user, initialProfile]);

  const handleEditProfile = () => {
    setIsEditing(true);
  };

  const handleSaveProfile = async (updatedProfile) => {
    if (!user) return;

    const result = await updateUserBasicInfo(user.uid, {
      name: updatedProfile.name,
      department: updatedProfile.department,
      batch: updatedProfile.batch,
      company: updatedProfile.company,
      location: updatedProfile.location,
      profilePic: profile.profilePic // Preserve profile picture
    });

    if (result.success) {
      setProfile(prev => ({
        ...prev,
        ...updatedProfile
      }));
      setIsEditing(false);
    } else {
      alert('Failed to update profile information');
    }
  };

  const handleUpdateProfile = (updatedProfile) => {
    setProfile(updatedProfile);
  };

  const handleUpdateAbout = async (aboutText) => {
    if (!user) return;

    const result = await updateUserProfileSection(user.uid, 'about', aboutText);
    if (result.success) {
      setProfile(prev => ({ ...prev, about: aboutText }));
    } else {
      alert('Failed to update about section');
    }
  };

  const handleUpdateExperience = async (timeline) => {
    if (!user) return;
    
    // Ensure each experience has an id
    const timelineWithIds = timeline.map(exp => ({
      ...exp,
      id: exp.id || Date.now()
    }));

    const result = await updateUserProfileSection(user.uid, 'timeline', timelineWithIds);
    if (result.success) {
      setProfile(prev => ({ ...prev, timeline: timelineWithIds }));
    } else {
      alert('Failed to update experience');
    }
  };

  const handleUpdateSkills = async (skills) => {
    if (!user) return;

    const result = await updateUserProfileSection(user.uid, 'skills', skills);
    if (result.success) {
      setProfile(prev => ({ ...prev, skills }));
    } else {
      alert('Failed to update skills');
    }
  };

  const handleUpdateAchievements = async (achievements) => {
    if (!user) return;

    const result = await updateUserProfileSection(user.uid, 'achievements', achievements);
    if (result.success) {
      setProfile(prev => ({ ...prev, achievements }));
    } else {
      alert('Failed to update achievements');
    }
  };

  const toggleTheme = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    document.documentElement.classList.toggle("dark", newDarkMode);
    localStorage.setItem("theme", newDarkMode ? "dark" : "light");
  };

  useEffect(() => {
    const isDark =
      localStorage.getItem("theme") === "dark" ||
      (!localStorage.getItem("theme") &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);
    setIsDarkMode(isDark);
    document.documentElement.classList.toggle("dark", isDark);
  }, []);

  useEffect(() => {
    // Simulate loading time for profile data
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  // Modify the profile picture update to save to Firebase
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file && user) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const imageUrl = reader.result;
        const result = await updateUserBasicInfo(user.uid, {
          profilePic: imageUrl
        });
        
        if (result.success) {
          setProfile(prev => ({
            ...prev,
            profilePic: imageUrl,
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <ThreeDots height="50" width="50" color="#3b82f6" ariaLabel="loading" />
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300">
      <div className="max-w-[1128px] mx-auto pt-[52px] px-4">
        <Card className="relative mb-2 overflow-hidden bg-white dark:bg-gray-800">
          {/* Background Cover with Text */}
          <div className="h-[200px] relative bg-gradient-to-r from-blue-400 to-blue-600 dark:from-blue-900 dark:to-indigo-900">
            {/* Alumni Text */}
            <div className="absolute inset-0 flex items-center justify-center"></div>

            {/* Theme Toggle - Adjusted z-index to appear above text */}
            <Button
              onClick={toggleTheme}
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 dark:bg-gray-800/50 
                dark:hover:bg-gray-700/50 text-white rounded-full p-2 z-10"
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5 text-yellow-300" />
              ) : (
                <Moon className="w-5 h-5 text-gray-100" />
              )}
            </Button>
          </div>

          {/* Profile Content */}
          {isEditing ? (
            <EditProfileForm
              profile={profile}
              onSave={handleSaveProfile}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <div className="px-8 pt-16 pb-8">
              <div className="flex flex-col md:flex-row items-start">
                {/* Profile Picture - Adjusted position */}
                <div className="relative -mt-32 md:mr-8">
                  {" "}
                  {/* Increased negative margin and added right margin */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <div
                    className="relative cursor-pointer group"
                    onClick={handleImageClick}
                    onMouseEnter={() => setImageHover(true)}
                    onMouseLeave={() => setImageHover(false)}
                  >
                    <Avatar className="w-32 h-32 border-4 border-white dark:border-gray-800 shadow-lg">
                      {" "}
                      {/* Added shadow */}
                      <AvatarImage
                        src={profile.profilePic}
                        alt={profile.name}
                        onError={(e) => {
                          e.target.src = "/android-chrome-512x512.png";
                        }}
                      />
                      <AvatarFallback>
                        {profile.name?.[0] || "U"}
                      </AvatarFallback>
                    </Avatar>

                    {/* Overlay on hover */}
                    <div
                      className={`absolute inset-0 flex items-center justify-center rounded-full bg-black/50 transition-opacity duration-200 ${
                        imageHover ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      <p className="text-white text-sm">Change Photo</p>
                    </div>
                  </div>
                </div>

                {/* Profile Details - Adjusted layout */}
                <div className="flex-1 mt-4 md:mt-0">
                  {" "}
                  {/* Removed margin top on desktop */}
                  <div className="space-y-3">
                    <div>
                      <h1 className="text-2xl md:text-3xl font-bold dark:text-white">
                        {profile.name}
                      </h1>
                      <h2 className="text-lg text-gray-600 dark:text-gray-300 mt-1">
                        {profile.college}
                      </h2>
                    </div>

                    <div className="flex flex-wrap gap-4 text-gray-600 dark:text-gray-300">
                      <div className="flex items-center gap-2">
                        <School className="w-4 h-4" />
                        <span>{profile.department || "Department"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CalendarClock className="w-4 h-4" />
                        <span>Batch of {profile.batch || "Year"}</span>
                      </div>
                      {profile.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{profile.location}</span>
                        </div>
                      )}
                      {profile.company && (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          <span>{profile.company}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={handleEditProfile}
                    variant="outline"
                    className="mt-4"
                  >
                    <Pencil className="w-4 h-4 mr-2" /> Edit Profile
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-4 text-gray-900 dark:text-gray-100">
          <div className="lg:col-span-3 space-y-4">
            <AboutSection profile={profile} onUpdate={handleUpdateAbout} />
            <ExperienceSection
              timeline={profile.timeline}
              onUpdate={handleUpdateExperience}
            />
          </div>
          <div className="space-y-4">
            <SkillsSection
              skills={profile.skills}
              onUpdate={handleUpdateSkills}
            />
            <AchievementsSection achievements={profile.achievements} onUpdate={handleUpdateAchievements} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Profile Form Component
const EditProfileForm = ({ profile, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: profile.name || '',
    department: profile.department || '',
    batch: profile.batch || '',
    company: profile.company || '',
    location: profile.location || '',
    // Keep existing profile data
    ...profile
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Pass all form data to parent
    onSave({
      ...profile, // Keep other profile data
      name: formData.name,
      department: formData.department,
      batch: formData.batch,
      company: formData.company,
      location: formData.location
    });
  };

  return (
    <div className="p-8">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Personal Info */}
        <div>
          <label className="block text-sm font-medium mb-1">Name</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        {/* Academic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Department</label>
            <Input
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Batch Year</label>
            <Input
              value={formData.batch}
              onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
              required
            />
          </div>
        </div>

        {/* Professional Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Company</label>
            <Input
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder="Current company"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Location</label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="City, Country"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit">Save Changes</Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileCard;

/* Changes made:
1. Added LinkedIn-style cover image with gradient
2. Larger profile picture with border
3. Improved layout spacing and organization
4. Better responsive grid system
5. Enhanced typography and spacing
6. More professional button placement
7. Improved dark mode implementation
8. Added education/company quick info
9. Better component organization
10. Added smooth transitions
11. Added Department and Batch Year information
12. Added college name display
13. Fixed profile information editing
14. Added image upload feature on profile picture hover/click
15. Added loading spinner while fetching profile data
*/
